import React from 'react';
import { SectionHeader } from './CandidateSummary';
import SearchSelect, { employeeOptions } from './SearchSelect';
import DateField, { MONTH_END, YEAR_START } from './DateField';
import NumberField from './NumberField';

/** xin_auto_mast keys the employee number series off these. */
export const EMP_TYPES = [
  { value: 1, label: 'Employee' },
  { value: 2, label: 'Doctor' },
  { value: 3, label: 'Housekeeping' },
  { value: 4, label: 'Security' },
  { value: 5, label: 'Canteen' },
  { value: 6, label: 'Intern' },
];

/** xin_employees.visitor_type is a tinyint code, not a label. */
export const VISITOR_TYPES = [
  { value: 1, label: 'Regular Employee' },
  { value: 2, label: 'Visiting Employee' },
];

/** xin_employees.employee_shift — attendance treats a nearby joiner differently. */
export const EMPLOYEE_SHIFTS = [
  { value: 1, label: 'Normal Shift' },
  { value: 2, label: 'Nearby Shift' },
];

/** Contractor details apply to nearby-shift joiners only. */
export const NEARBY_SHIFT = 2;

/**
 * The joining block. Filling this in and saving creates the employee record,
 * the approval mapping and the joining month's duty roster, so the candidate
 * leaves recruitment as a live employee.
 */
const JoiningFields = ({ form, onChange, shifts, hods, contractors = [], contractTypes = [] }) => {
  const nearby = String(form.employee_shift) === String(NEARBY_SHIFT);

  // Leaving a stale contractor behind on a normal-shift joiner would save a
  // value the form no longer shows, so switching away clears both fields.
  const changeEmployeeShift = (value) => {
    onChange('employee_shift', value);

    if (String(value) !== String(NEARBY_SHIFT)) {
      onChange('contractor', '');
      onChange('contractor_type', '');
    }
  };

  return (
  <div className="border rounded p-3 mb-3">
    <SectionHeader title="Joining Details" />

    <div className="row">
      <div className="col-md-4 form-group required-tooltip">
        <label>Date of Joining</label>
        <DateField
          value={form.joined_date || ''}
          onChange={(v) => onChange('joined_date', v)}
          minDate={YEAR_START}
          maxDate={MONTH_END}
          required
        />
      </div>

      <div className="col-md-4 form-group">
        <label>Expected Date of Joining</label>
        <DateField
          value={form.expected_joined_date || ''}
          onChange={(v) => onChange('expected_joined_date', v)}
          minDate={YEAR_START}
          maxDate={MONTH_END}
        />
      </div>

      <div className="col-md-4 form-group required-tooltip">
        <label>Shift</label>
        <SearchSelect
          value={form.shift_code}
          onChange={(v) => onChange('shift_code', v)}
          options={shifts.map((shift) => ({
            value: shift.shift_code,
            label: `${shift.shift_code} — ${shift.shift_name}`,
          }))}
          placeholder="Search shift…"
          required
        />
      </div>

      <div className="col-md-4 form-group required-tooltip">
        <label>Visiting Type</label>
        <select
          className="form-control required-input"
          value={form.visitor_type || ''}
          onChange={(e) => onChange('visitor_type', e.target.value)}
        >
          <option value="">Choose</option>
          {VISITOR_TYPES.map((type) => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      <div className="col-md-4 form-group">
        <label>Employee Type</label>
        <select
          className="form-control"
          value={form.emp_type || 1}
          onChange={(e) => onChange('emp_type', e.target.value)}
        >
          {EMP_TYPES.map((type) => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      <div className="col-md-4 form-group">
        <label>Probation Days</label>
        <NumberField
          value={form.prob_days ?? 90}
          onChange={(v) => onChange('prob_days', v)}
        />
      </div>

      <div className="col-md-4 form-group">
        <label>HOD</label>
        <SearchSelect
          value={form.hod_id}
          onChange={(v) => onChange('hod_id', v)}
          options={employeeOptions(hods)}
          placeholder="Search HOD…"
        />
      </div>

      <div className="col-md-4 form-group required-tooltip">
        <label>Employee Shift</label>
        <select
          className="form-control required-input"
          value={form.employee_shift || ''}
          onChange={(e) => changeEmployeeShift(e.target.value)}
        >
          <option value="">Select Employee Shift</option>
          {EMPLOYEE_SHIFTS.map((shift) => (
            <option key={shift.value} value={shift.value}>{shift.label}</option>
          ))}
        </select>
      </div>

      {/* xin_employees.contractor — contractor_name_title.id. Nearby only. */}
      <div className={`col-md-4 form-group${nearby ? ' required-tooltip' : ''}`}>
        <label>Contractor</label>
        <select
          className={`form-control${nearby ? ' required-input' : ''}`}
          value={form.contractor || ''}
          onChange={(e) => onChange('contractor', e.target.value)}
          disabled={!nearby}
        >
          <option value="">Choose</option>
          {contractors.map((contractor) => (
            <option key={contractor.id} value={contractor.id}>{contractor.contractor_name}</option>
          ))}
        </select>
      </div>

      {/* xin_employees.contractor_type — Permanent / Probation. Nearby only. */}
      <div className={`col-md-4 form-group${nearby ? ' required-tooltip' : ''}`}>
        <label>Contract Type</label>
        <select
          className={`form-control${nearby ? ' required-input' : ''}`}
          value={form.contractor_type || ''}
          onChange={(e) => onChange('contractor_type', e.target.value)}
          disabled={!nearby}
        >
          <option value="">Choose</option>
          {contractTypes.map((type) => (
            <option key={type.contract_type_id} value={type.contract_type_id}>{type.name}</option>
          ))}
        </select>
      </div>

      <div className="col-md-12 form-group">
        <label>Joining Notes</label>
        <textarea
          className="form-control"
          rows="2"
          value={form.join_notes || ''}
          onChange={(e) => onChange('join_notes', e.target.value)}
        />
      </div>
    </div>
  </div>
  );
};

export default JoiningFields;
